import { useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useGuestAccessHash } from 'utils/hooks/useGuestAccessHash';
import { getVirtualSessionsApiClient } from 'lib/SplashApiClient/subclients/VirtualSessionsApiClient';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { useNotificationManager } from 'common/compounds/NotificationManager';
import uniqueId from 'lodash/uniqueId';
import { SuiNotification } from '@splashthat/sui';

export const useSessionRegistration = () => {
    const virtualSessionsApiClient = useRef(getVirtualSessionsApiClient()).current;
    const { event } = useTouchpointData();
    const { guestAccessHash } = useGuestAccessHash(`sessionsRegistration/${event.id}/guestAccessHash`);
    const notificationManager = useNotificationManager();
    const queryClient = useQueryClient();
    const queryKey = ['sessionRegistration', event.id];
    const isQueryEnabled = typeof guestAccessHash === 'string';
    const { data: guestSessionRegistrationStatuses, isLoading } = useQuery(
        queryKey,
        () => virtualSessionsApiClient.getAttendeeSessionsStatuses({ eventId: event.id, guestAccessHash }),
        {
            enabled: isQueryEnabled,
        },
    );
    const ALREADY_BOOKED_TEXT =
        'You have already booked a session at this time! Change your schedule to book this session.';
    const UNKNOWN_ERROR_TEXT = 'There was an error registering for this session. Please try again.';

    const registerForSessionMutation = useMutation(
        (sessionId) => virtualSessionsApiClient.registerForSession({ eventId: event.id, sessionId, guestAccessHash }),
        {
            onSuccess: () => {
                setTimeout(() => {
                    queryClient.invalidateQueries(queryKey);
                }, 1000);
            },
            onError: (error) => {
                const errorMessage = error?.status === 409 ? ALREADY_BOOKED_TEXT : UNKNOWN_ERROR_TEXT;
                notificationManager.showNotification({
                    id: uniqueId('SESSION_REGISTRATION_ERROR_'),
                    message: errorMessage,
                    variation: SuiNotification.ERROR,
                });
            },
        },
    );

    const unregisterForSessionMutation = useMutation(
        (sessionId) => virtualSessionsApiClient.unregisterForSession({ eventId: event.id, sessionId, guestAccessHash }),
        {
            onSuccess: () => {
                setTimeout(() => {
                    queryClient.invalidateQueries(queryKey);
                }, 1000);
            },
        },
    );

    return {
        guestSessionRegistrationStatuses,
        isSessionRegistrationReady: !isQueryEnabled || (isQueryEnabled && !isLoading),
        isRegistering: registerForSessionMutation.isLoading,
        isRegisteringSuccess: registerForSessionMutation.isSuccess,
        isUnregistering: unregisterForSessionMutation.isLoading,
        isUnregisteringSuccess: unregisterForSessionMutation.isSuccess,
        actions: {
            registerForSession: (sessionId) => registerForSessionMutation.mutate(sessionId),
            unregisterForSession: (sessionId) => unregisterForSessionMutation.mutate(sessionId),
        },
    };
};
